import React from "react";
import { Button } from "semantic-ui-react";
import ReactTable, { ReactTableDefaults } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/pro-regular-svg-icons";
import { saveAs } from "file-saver";
import moment from "moment";

import { combineCountAndPercent, exportCountAndPercentHeaders, exportCountAndPercentValues } from "../../../util/common";

const CommunicationsOverview = ({ report, t }) => {
  if (!report.report_data) {
    return (
      <>
        <h3 className="report-heading">{t(report.report_name).message || report.report_name}</h3>
        <p className="no-data">{t("no_data").message || "No data"}</p>
      </>
    );
  }

  const data = report.report_data.map(d => JSON.parse(d));

  const exportData = async () => {
    let csv = "";
    let header = (t("location").message || "Location") + ", ";
    header += (t("wo_count").message || "WO Count") + ", ";
    header += exportCountAndPercentHeaders(t("answered_desk_checkin").message || "Answered desk check-in", t) + ", ";
    header += exportCountAndPercentHeaders(t("online_check_in").message || "Online check-in", t) + ", ";
    header += exportCountAndPercentHeaders(t("online_check_in_opened").message || "Online Check-In Opened", t) + ", ";
    header += exportCountAndPercentHeaders(t("online_check_in_answered").message || "Online Check-In Answered", t) + ", ";
    header += exportCountAndPercentHeaders(t("total_check_in_answered").message || "Total Check-in Answered", t) + ", ";
    header += exportCountAndPercentHeaders(t("diagnose_overview").message || "Diagnose Overview", t) + ", ";
    header += exportCountAndPercentHeaders(t("diagnose_overview_answered").message || "Diagnose Overview Answered", t) + ", ";
    header += exportCountAndPercentHeaders(t("opened_diagnose_overview").message || "Diagnose Overview Opened", t) + ", ";
    header += exportCountAndPercentHeaders(t("repair_overview").message || "Repair overview", t) + ", ";
    header += exportCountAndPercentHeaders(t("opened_repair_overview").message || "Repair Overview Opened", t);

    let values = data
      .map(
        d =>
          d.location +
          ", " +
          d.wo_count +
          ", " +
          exportCountAndPercentValues(d.answered_desk_checkin_wo_count, d.answered_desk_checkin_wo_percent) +
          ", " +
          exportCountAndPercentValues(d.online_checkin_wo_count, d.online_checkin_wo_percent) +
          ", " +
          exportCountAndPercentValues(d.opened_online_checkin, d.opened_online_checkin_percent) +
          ", " +
          exportCountAndPercentValues(d.answered_online_checkin, d.answered_online_checkin_percent) +
          ", " +
          exportCountAndPercentValues(d.answered_checkin_wo_count, d.answered_checkin_wo_percent) +
          ", " +
          exportCountAndPercentValues(d.diagnose_overview_wo_count, d.diagnose_overview_wo_percent) +
          ", " +
          exportCountAndPercentValues(d.answered_diagnose_overview, d.answered_diagnose_overview_percent) +
          ", " +
          exportCountAndPercentValues(d.opened_diagnose_overview, d.opened_diagnose_overview_percent) +
          ", " +
          exportCountAndPercentValues(d.repair_overview_wo_count, d.repair_overview_wo_percent) +
          ", " +
          exportCountAndPercentValues(d.opened_repair_overview, d.opened_repair_overview_percent)
      )
      .join("\n");

    csv += header + "\n" + values;
    saveAs(new File([csv], { type: "text/csv;charset=utf-8" }), "CommunicationsOverview_" + moment().format("YYYYMMDD") + ".csv");
  };

  return (
    <>
      <h3 className="report-heading">
        {t(report.report_name).message || report.report_name}
        <Button color="green" floated="right" basic onClick={() => exportData()}>
          <span style={{ marginRight: "10px" }}>{t("export").message || "Export"}</span>
          <FontAwesomeIcon icon={faFileExport} />
        </Button>
      </h3>
      <div className="dealer-reports-table-container">
        <ReactTable
          className="ReactTable -floated-table no-overflow all-data-table -initial-margin"
          data={data}
          showPagination={false}
          showPageSizeOptions={false}
          sortable={false}
          style={{ maxHeight: "600px" }}
          resizable={false}
          defaultPageSize={data.length || 0}
          pageSize={data.length || 0}
          noDataText={
            <div className="Table__no-results">
              <p>{t("no_data").message || "No data"}</p>
            </div>
          }
          column={{
            ...ReactTableDefaults.column,
            headerClassName: "ReactTable__column-header dealer-table-header",
            className: "ReactTable__column",
          }}
          columns={[
            {
              Header: <strong>{t("location").message || "Location"}</strong>,
              accessor: "location",
            },
            {
              Header: <strong>{t("wo_count").message || "WO Count"}</strong>,
              accessor: "wo_count",
            },
            {
              Header: <strong>{t("answered_desk_checkin_wo_percent").message || "Desk Check-in Answered (WO %)"}</strong>,
              id: "answered_desk_checkin_wo",
              accessor: d => combineCountAndPercent(d.answered_desk_checkin_wo_count, d.answered_desk_checkin_wo_percent),
            },
            {
              Header: <strong>{t("online_check_in_(wo_%)").message || "Online Check-In (WO%)"}</strong>,
              id: "online_checkin_wo",
              accessor: d => combineCountAndPercent(d.online_checkin_wo_count, d.online_checkin_wo_percent),
            },
            {
              Header: <strong>{t("online_check_in_opened").message || "Online Check-In Opened"}</strong>,
              id: "opened_online_checkin",
              accessor: d => combineCountAndPercent(d.opened_online_checkin, d.opened_online_checkin_percent),
            },
            {
              Header: <strong>{t("online_check_in_answered").message || "Online Check-In Answered"}</strong>,
              id: "answered_online_checkin",
              accessor: d => combineCountAndPercent(d.answered_online_checkin, d.answered_online_checkin_percent),
            },
            {
              Header: <strong>{t("total_check_in_answered_(wo_%)").message || "Total Check-In Answered (WO%)"}</strong>,
              id: "answered_checkin_wo",
              accessor: d => combineCountAndPercent(d.answered_checkin_wo_count, d.answered_checkin_wo_percent),
            },
            {
              Header: <strong>{t("diagnose_overview_(wo_%)").message || "Diagnose Overview (WO %)"}</strong>,
              id: "diagnose_overview_wo",
              accessor: d => combineCountAndPercent(d.diagnose_overview_wo_count, d.diagnose_overview_wo_percent),
            },
            {
              Header: <strong>{t("opened_diagnose_overview").message || "Diagnose Overview Opened"}</strong>,
              id: "opened_diagnose_overview",
              accessor: d => combineCountAndPercent(d.opened_diagnose_overview, d.opened_diagnose_overview_percent),
            },
            {
              Header: <strong>{t("diagnose_overview_answered").message || "Diagnose Overview Answered"}</strong>,
              id: "answered_diagnose_overview",
              accessor: d => combineCountAndPercent(d.answered_diagnose_overview, d.answered_diagnose_overview_percent),
            },
            {
              Header: <strong>{t("repair_overview_(wo_%)").message || "Repair Overview (WO %)"}</strong>,
              id: "repair_overview_wo",
              accessor: d => combineCountAndPercent(d.repair_overview_wo_count, d.repair_overview_wo_percent),
            },
            {
              Header: <strong>{t("opened_repair_overview").message || "Repair Overview Opened"}</strong>,
              id: "opened_repair_overview",
              accessor: d => combineCountAndPercent(d.opened_repair_overview, d.opened_repair_overview_percent),
            },
          ]}
        />
      </div>
    </>
  );
};

export { CommunicationsOverview };
